// ============================================
// Critical Safety Rules — Always Applied
// These rules run even if Firestore is unavailable.
// They represent life-threatening or serious interactions
// that must NEVER be bypassed by the LLM.
// ============================================

import type { SafetyRule } from '../types/formulary';

export const CRITICAL_SAFETY_RULES: SafetyRule[] = [
  // --- LIFE-THREATENING BLOCKS ---

  {
    id: 'critical-nitrates-pde5',
    name: 'Nitrates + PDE5 Inhibitors',
    severity: 'block',
    triggerType: 'drug_interaction',
    matchMedications: [
      'nitroglycerin', 'isosorbide mononitrate', 'isosorbide dinitrate',
      'nitrates', 'nitro', 'imdur', 'isordil',
    ],
    affectedProductIds: ['rx-tadalafil', 'rx-sildenafil'],
    message: 'BLOCKED: Nitrates combined with tadalafil or sildenafil can cause severe, life-threatening hypotension. This combination is absolutely contraindicated.',
    clinicalNote: 'Concurrent nitrate therapy is an absolute contraindication for PDE5 inhibitors. Risk of fatal hypotension. No exceptions.',
    isActive: true,
    updatedAt: '2025-01-01T00:00:00Z',
  },

  {
    id: 'critical-ssri-methylene-blue',
    name: 'SSRIs/SNRIs + Methylene Blue',
    severity: 'block',
    triggerType: 'drug_interaction',
    matchMedications: [
      'sertraline', 'fluoxetine', 'paroxetine', 'citalopram', 'escitalopram',
      'fluvoxamine', 'venlafaxine', 'duloxetine', 'desvenlafaxine',
      'ssri', 'snri', 'zoloft', 'prozac', 'paxil', 'lexapro', 'celexa',
      'effexor', 'cymbalta', 'pristiq',
    ],
    affectedProductIds: ['methylene-blue'],
    message: 'BLOCKED: Methylene blue is a potent MAO-A inhibitor. Combined with SSRIs/SNRIs, it causes serotonin syndrome — a potentially fatal emergency.',
    clinicalNote: 'Methylene blue inhibits MAO-A. Serotonin syndrome risk is well-documented and potentially fatal. Must discontinue SSRI ≥5 half-lives before methylene blue (fluoxetine requires 5-6 weeks).',
    isActive: true,
    updatedAt: '2025-01-01T00:00:00Z',
  },

  {
    id: 'critical-maoi-methylene-blue',
    name: 'MAOIs + Methylene Blue',
    severity: 'block',
    triggerType: 'drug_interaction',
    matchMedications: [
      'phenelzine', 'tranylcypromine', 'isocarboxazid', 'selegiline',
      'maoi', 'nardil', 'parnate', 'marplan', 'emsam',
    ],
    affectedProductIds: ['methylene-blue'],
    message: 'BLOCKED: Methylene blue with MAOIs causes serotonin syndrome — a life-threatening emergency.',
    clinicalNote: 'Both are MAO inhibitors. Combination is absolutely contraindicated. Requires extended washout period.',
    isActive: true,
    updatedAt: '2025-01-01T00:00:00Z',
  },

  {
    id: 'critical-opioid-naltrexone',
    name: 'Active Opioid Use + Naltrexone',
    severity: 'block',
    triggerType: 'drug_interaction',
    matchMedications: [
      'oxycodone', 'hydrocodone', 'morphine', 'fentanyl', 'codeine',
      'tramadol', 'methadone', 'buprenorphine', 'opioid', 'opioids',
      'percocet', 'vicodin', 'norco', 'suboxone', 'subutex',
    ],
    affectedProductIds: ['rx-low-dose-naltrexone'],
    message: 'BLOCKED: Naltrexone blocks opioid receptors. If you are currently taking opioids, naltrexone will cause precipitated withdrawal — a medical emergency.',
    clinicalNote: 'Patient must be opioid-free for minimum 7-10 days before starting naltrexone (even LDN). Precipitated withdrawal is severe and potentially dangerous.',
    isActive: true,
    updatedAt: '2025-01-01T00:00:00Z',
  },

  // --- CANCER RISK BLOCKS ---

  {
    id: 'critical-breast-cancer-estradiol',
    name: 'Breast Cancer History + Estrogen',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: [
      'breast cancer', 'breast cancer history', 'er+ breast cancer',
      'hormone receptor positive breast cancer', 'brca',
      'breast ca', 'ca breast', 'hx breast cancer',
    ],
    affectedProductIds: ['rx-estradiol-patch', 'rx-progesterone-prometrium', 'rx-testosterone-cream-female'],
    message: 'BLOCKED: Estrogen and progesterone therapy is contraindicated in patients with a history of hormone receptor-positive breast cancer. HRT may be reconsidered with documented oncology clearance after extended remission.',
    clinicalNote: 'Estrogen/progesterone therapy may stimulate ER+/PR+ breast cancer growth. Requires documented oncology clearance before any HRT consideration. May be reconsidered after extended remission with oncologist sign-off.',
    isActive: true,
    updatedAt: '2025-01-01T00:00:00Z',
  },

  {
    id: 'critical-mtc-glp1',
    name: 'MTC/MEN2 Family History + GLP-1 Agonists',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: [
      'medullary thyroid cancer', 'mtc', 'men2', 'men 2',
      'multiple endocrine neoplasia', 'family history mtc',
      'family history thyroid cancer',
    ],
    affectedProductIds: ['rx-semaglutide', 'rx-tirzepatide'],
    message: 'BLOCKED: GLP-1 receptor agonists are contraindicated in patients with personal or family history of medullary thyroid carcinoma or MEN2 syndrome.',
    clinicalNote: 'Black box warning on semaglutide/tirzepatide. Thyroid C-cell tumor risk in rodent studies. Absolute contraindication with MTC/MEN2 history.',
    isActive: true,
    updatedAt: '2025-01-01T00:00:00Z',
  },

  // --- ORGAN DAMAGE BLOCKS ---

  {
    id: 'critical-g6pd-methylene-blue',
    name: 'G6PD Deficiency + Methylene Blue',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: ['g6pd deficiency', 'g6pd', 'glucose-6-phosphate dehydrogenase deficiency'],
    affectedProductIds: ['methylene-blue'],
    message: 'BLOCKED: Methylene blue causes severe hemolytic anemia in patients with G6PD deficiency.',
    clinicalNote: 'Methylene blue is an oxidizing agent that triggers hemolysis in G6PD-deficient patients. Screen for G6PD before prescribing. Absolute contraindication.',
    isActive: true,
    updatedAt: '2025-01-01T00:00:00Z',
  },

  {
    id: 'critical-hemochromatosis-iron',
    name: 'Hemochromatosis + Iron Supplementation',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: ['hemochromatosis', 'iron overload', 'high ferritin'],
    affectedProductIds: ['supp-iron-bisglycinate'],
    message: 'BLOCKED: Iron supplementation is contraindicated in hemochromatosis — it worsens iron overload and increases organ damage risk.',
    clinicalNote: 'Check ferritin and transferrin saturation. Hemochromatosis patients need therapeutic phlebotomy, not supplementation.',
    isActive: true,
    updatedAt: '2025-01-01T00:00:00Z',
  },

  {
    id: 'critical-liver-disease-metformin',
    name: 'Severe Liver Disease + Metformin',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: ['liver disease', 'hepatic impairment', 'cirrhosis', 'liver failure'],
    affectedProductIds: ['rx-metformin-er'],
    message: 'BLOCKED: Metformin is contraindicated in severe hepatic impairment due to increased risk of lactic acidosis.',
    clinicalNote: 'Impaired hepatic lactate clearance increases lactic acidosis risk. Mild hepatic impairment may be acceptable with monitoring — requires provider judgment.',
    isActive: true,
    updatedAt: '2025-01-01T00:00:00Z',
  },

  {
    id: 'critical-renal-metformin',
    name: 'Severe Renal Impairment + Metformin',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: ['renal failure', 'kidney failure', 'egfr below 30', 'ckd stage 4', 'ckd stage 5', 'dialysis'],
    affectedProductIds: ['rx-metformin-er'],
    message: 'BLOCKED: Metformin is contraindicated with eGFR < 30 mL/min due to risk of lactic acidosis.',
    clinicalNote: 'Contraindicated at eGFR <30. Dose reduction required at eGFR 30-45. Monitor renal function regularly.',
    isActive: true,
    updatedAt: '2025-01-01T00:00:00Z',
  },

  // --- PREGNANCY BLOCKS (Category X) ---

  {
    id: 'critical-pregnancy-tretinoin',
    name: 'Pregnancy + Tretinoin',
    severity: 'block',
    triggerType: 'pregnancy',
    matchConditions: ['pregnant', 'pregnancy', 'trying to conceive', 'ttc'],
    affectedProductIds: ['rx-tretinoin'],
    message: 'BLOCKED: Tretinoin is FDA Category X — causes severe birth defects. Absolutely contraindicated in pregnancy or if planning pregnancy.',
    clinicalNote: 'Category X teratogen. Retinoids cause craniofacial, cardiac, and CNS malformations. Requires negative pregnancy test and contraception.',
    isActive: true,
    updatedAt: '2025-01-01T00:00:00Z',
  },

  {
    id: 'critical-pregnancy-finasteride',
    name: 'Pregnancy + Finasteride/Dutasteride',
    severity: 'block',
    triggerType: 'pregnancy',
    matchConditions: ['pregnant', 'pregnancy', 'trying to conceive', 'ttc'],
    affectedProductIds: ['rx-finasteride', 'rx-dutasteride'],
    message: 'BLOCKED: 5-alpha reductase inhibitors are Category X — cause abnormalities in male fetus genitalia. Women of childbearing age should not even handle crushed tablets.',
    clinicalNote: 'Category X. Finasteride/dutasteride can be absorbed through skin. Female partners of men on these medications should avoid contact with crushed/broken tablets.',
    isActive: true,
    updatedAt: '2025-01-01T00:00:00Z',
  },

  {
    id: 'critical-pregnancy-rapamycin',
    name: 'Pregnancy + Rapamycin',
    severity: 'block',
    triggerType: 'pregnancy',
    matchConditions: ['pregnant', 'pregnancy', 'trying to conceive', 'ttc'],
    affectedProductIds: ['rapamycin'],
    message: 'BLOCKED: Rapamycin (sirolimus) is an immunosuppressant with known teratogenic potential. Contraindicated in pregnancy.',
    clinicalNote: 'mTOR inhibitors are teratogenic. Requires reliable contraception during use and for 12 weeks after discontinuation.',
    isActive: true,
    updatedAt: '2025-01-01T00:00:00Z',
  },

  // --- ALLERGY BLOCKS ---

  {
    id: 'critical-fish-allergy-omega3',
    name: 'Fish Allergy + Omega-3 Fish Oil',
    severity: 'block',
    triggerType: 'allergy',
    matchAllergens: ['fish', 'fish oil', 'shellfish', 'seafood'],
    affectedProductIds: ['supp-omega-3-fish-oil'],
    message: 'BLOCKED: Fish oil supplements are derived from fish and may trigger allergic reactions in patients with fish allergy. Consider algae-based omega-3 alternatives.',
    clinicalNote: 'Fish-derived omega-3s contraindicated. Recommend algae-based EPA/DHA as alternative if patient needs anti-inflammatory omega-3 support.',
    isActive: true,
    updatedAt: '2025-01-01T00:00:00Z',
  },

  {
    id: 'critical-peanut-allergy-progesterone',
    name: 'Peanut Allergy + Prometrium',
    severity: 'block',
    triggerType: 'allergy',
    matchAllergens: ['peanut', 'peanuts', 'peanut oil'],
    affectedProductIds: ['rx-progesterone-prometrium'],
    message: 'BLOCKED: Prometrium (micronized progesterone) capsules contain peanut oil. Contraindicated in patients with peanut allergy.',
    clinicalNote: 'Prometrium specifically contains peanut oil as excipient. Alternative progesterone formulations (compounded, vaginal) do not contain peanut oil.',
    isActive: true,
    updatedAt: '2025-01-01T00:00:00Z',
  },

  // --- VTE/DVT BLOCK ---

  {
    id: 'critical-vte-hrt',
    name: 'VTE/DVT History + HRT',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: [
      'dvt', 'deep vein thrombosis', 'pulmonary embolism', 'pe',
      'blood clot', 'blood clots', 'thromboembolism', 'vte',
      'venous thromboembolism', 'factor v leiden', 'protein c deficiency',
      'protein s deficiency', 'clotting disorder', 'thrombophilia',
      'antiphospholipid syndrome',
    ],
    affectedProductIds: ['rx-estradiol-patch'],
    message: 'BLOCKED: Estrogen therapy is contraindicated in patients with history of venous thromboembolism (DVT/PE). Transdermal estrogen carries lower but non-zero clotting risk. Requires hematology clearance. Note: Progesterone alone does not significantly increase VTE risk and may be considered separately.',
    clinicalNote: 'VTE risk with estrogen is well-established. Transdermal route has lower risk than oral but is NOT zero risk. Progesterone monotherapy has minimal VTE impact and is not blocked. Patients with Factor V Leiden, protein C/S deficiency, or antiphospholipid syndrome have compounded risk. Requires documented hematology clearance before any estrogen therapy.',
    isActive: true,
    updatedAt: '2025-01-01T00:00:00Z',
  },

  // --- CANCER RISK BLOCKS (HRT) ---

  {
    id: 'critical-ovarian-cancer-hrt',
    name: 'Ovarian Cancer History + HRT',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: [
      'ovarian cancer', 'ovarian cancer history', 'history of ovarian cancer',
      'hormone-sensitive cancer', 'hormone sensitive cancer',
      'cancer history ovarian', 'ovarian carcinoma',
      'ovarian ca', 'ca ovary', 'hx ovarian cancer',
    ],
    affectedProductIds: ['rx-estradiol-patch', 'rx-progesterone-prometrium', 'rx-testosterone-cream-female'],
    message: 'BLOCKED: Estrogen, progesterone, and testosterone therapy are contraindicated in patients with a history of ovarian cancer due to hormone-sensitivity risk. HRT may be reconsidered with documented oncology clearance after extended remission.',
    clinicalNote: 'Ovarian cancer is frequently hormone-sensitive. Exogenous estrogen/progesterone may stimulate residual or recurrent disease. Testosterone aromatizes to estradiol. Requires documented oncology clearance before any HRT consideration. May be reconsidered after extended remission with oncologist sign-off.',
    isActive: true,
    updatedAt: '2025-01-01T00:00:00Z',
  },

  {
    id: 'critical-endometrial-cancer-hrt',
    name: 'Endometrial Cancer History + HRT',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: [
      'endometrial cancer', 'endometrial cancer history', 'history of endometrial cancer',
      'uterine cancer', 'uterine cancer history', 'cancer of the uterus',
      'endometrial carcinoma',
      'endometrial ca', 'ca uterus', 'hx endometrial cancer',
    ],
    affectedProductIds: ['rx-estradiol-patch', 'rx-progesterone-prometrium', 'rx-testosterone-cream-female'],
    message: 'BLOCKED: Estrogen and progesterone therapy are contraindicated in patients with a history of endometrial cancer.',
    clinicalNote: 'Endometrial cancer is estrogen-driven. Exogenous estrogen promotes endometrial proliferation and recurrence risk. Progesterone alone is insufficient protection with cancer history. Requires oncology clearance.',
    isActive: true,
    updatedAt: '2025-01-01T00:00:00Z',
  },

  // --- LIVER CANCER + HRT ---

  {
    id: 'critical-liver-cancer-hrt',
    name: 'Liver Cancer/Disease + Estrogen',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: [
      'liver cancer', 'hepatocellular carcinoma', 'hcc',
      'hepatic cancer', 'liver tumor',
    ],
    affectedProductIds: ['rx-estradiol-patch', 'rx-progesterone-prometrium'],
    message: 'BLOCKED: Estrogen therapy is contraindicated in patients with liver cancer or hepatocellular carcinoma. Estrogen is hepatically metabolized and may promote tumor growth.',
    clinicalNote: 'Estrogen undergoes extensive first-pass hepatic metabolism (even transdermal has hepatic effects). Liver malignancy is a contraindication for all forms of HRT.',
    isActive: true,
    updatedAt: '2025-01-01T00:00:00Z',
  },
];
